import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Phone, MapPin, Menu, X, ShoppingBag, Heart } from "lucide-react";
import { STORE } from "../data/store";
import { useCart } from "../context/CartContext";
import { useFavorites } from "../context/FavoritesContext";
import { useSettings } from "../context/SettingsContext";
import { assetUrl } from "../lib/api";

export const Header = ({ search, setSearch, onNav }) => {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const { count: favCount } = useFavorites();
  const { count, setOpen: openCart } = useCart();
  const links = [
    { k: "inmobiliario", label: "Inmobiliario" },
    { k: "automotivo", label: "Automotivo" },
    { k: "industrial", label: "Industrial" },
    { k: "accesorios", label: "Accesorios" },
    { k: "promos", label: "Ofertas" },
    { k: "calc", label: "Calculadora" },
    { k: "colores", label: "Colores" },
    { k: "lab", label: "Laboratorio" },
    { k: "galeria", label: "Galería" },
    { k: "contacto", label: "Contacto" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-4 h-20">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 shrink-0">
            <img
              data-testid="brand-logo-img" src={settings.logo_url ? assetUrl(settings.logo_url) : "/logo.png"}
              alt={`${STORE.name} — ${STORE.slogan}`}
              className="h-[68px] w-auto max-w-[220px] object-contain"
            />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-[#0A3977] text-base tracking-tight">{STORE.name}</div>
              <div data-testid="brand-slogan" className="text-[10px] font-bold tracking-widest uppercase text-[#E02424]">{STORE.slogan}</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5 ml-2">
            {links.map((l) => (
              <button
                key={l.k}
                data-testid={`nav-${l.k}`}
                onClick={() => onNav(l.k)}
                className="px-2.5 py-2 text-[13px] font-semibold text-gray-700 hover:text-[#0A3977] hover:bg-blue-50 rounded-md transition-colors whitespace-nowrap"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2 ml-auto w-44 2xl:w-64 shrink-0">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar producto o código..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-100 rounded-lg border border-transparent focus:border-[#0067B1] focus:bg-white outline-none transition-colors"
              />
            </div>
          </div>

          <Link
            to="/admin"
            data-testid="admin-portal-link"
            className="hidden md:inline-flex ml-2 px-3 py-2 text-xs font-bold text-[#0A3977] border border-[#0A3977] rounded-md hover:bg-[#0A3977] hover:text-white transition-colors"
          >
            Admin
          </Link>

          <button
            data-testid="favorites-open-btn"
            onClick={() => onNav("favoritos")}
            className="relative ml-auto md:ml-2 p-2 rounded-lg text-[#E02424] hover:bg-red-50 transition-colors"
            aria-label="Ver favoritos"
          >
            <Heart className="w-6 h-6" />
            {favCount > 0 && (
              <span data-testid="favorites-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E02424] text-white text-[10px] font-bold flex items-center justify-center">
                {favCount}
              </span>
            )}
          </button>

          <button
            data-testid="cart-open-btn"
            onClick={() => openCart(true)}
            className="relative p-2 rounded-lg text-[#0A3977] hover:bg-blue-50 transition-colors"
            aria-label="Ver pedido"
          >
            <ShoppingBag className="w-6 h-6" />
            {count > 0 && (
              <span data-testid="cart-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E02424] text-white text-[10px] font-bold flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          <button data-testid="mobile-menu-btn" className="xl:hidden p-2" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <div className="md:hidden pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              data-testid="search-input-mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar producto o código..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-gray-100 rounded-lg outline-none"
            />
          </div>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-gray-200 bg-white px-4 py-3 space-y-1">
          {links.map((l) => (
            <button
              key={l.k}
              data-testid={`nav-mobile-${l.k}`}
              onClick={() => { onNav(l.k); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-blue-50 rounded-md"
            >
              {l.label}
            </button>
          ))}
          <Link to="/admin" className="block px-3 py-2 text-sm font-bold text-[#0A3977]">Panel Admin</Link>
          <a href={`tel:${STORE.whatsapp}`} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600">
            <Phone className="w-4 h-4" /> {STORE.phoneDisplay}
          </a>
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600">
            <MapPin className="w-4 h-4" /> {STORE.address}
          </div>
        </div>
      )}
    </header>
  );
};
